import { ConnectorSelection, isSameConnectorSelection } from '../../connectorSelection'

/** Fields of one anchor row to replace; omitted fields keep their saved value. */
export interface AnchorEdit {
  manualLoads?: ManualLoad[]
  comment?: string
  connectorSelection?: ConnectorSelection
}

/** Edits keyed by support guid. */
export type AnchorEdits = Record<string, AnchorEdit>

export const connectorSelectionOfRow = (
  row: Pick<AnchorInterventionsTableRowData, 'connectorSelectionPolicy' | 'userDefinedConnectorId'>,
): ConnectorSelection => ({
  policy: row.connectorSelectionPolicy,
  connectorId: row.userDefinedConnectorId ?? null,
})

/**
 * The full save payload: the endpoint replaces every support at once, so every saved row is
 * replayed and only the edited fields of the edited rows are swapped in.
 */
export const buildManualLoadsPayload = (
  rows: AnchorInterventionsTableRowData[],
  edits: AnchorEdits,
): ManualLoadOnSupport[] =>
  rows.map(row => {
    const edit = edits[row.support_guid]
    const selection = edit?.connectorSelection ?? connectorSelectionOfRow(row)
    return {
      support_id: row.support_guid,
      manual_loads: edit?.manualLoads ?? row.manual_loads,
      comment: edit?.comment ?? row.comment,
      connectorSelectionPolicy: selection.policy,
      userDefinedConnectorId: selection.connectorId,
    }
  })

/** True when the edits change what the anchor check reads: the loads or the Zuganker. */
export const editsChangeCheckInputs = (
  rows: AnchorInterventionsTableRowData[],
  edits: AnchorEdits,
): boolean =>
  rows.some(row => {
    const edit = edits[row.support_guid]
    if (!edit) return false
    const loadsChanged =
      !!edit.manualLoads &&
      (edit.manualLoads.length !== row.manual_loads.length ||
        edit.manualLoads.some(
          (load, index) =>
            load.load !== row.manual_loads[index]?.load ||
            load.load_formula !== row.manual_loads[index]?.load_formula ||
            load.source !== row.manual_loads[index]?.source,
        ))
    const selectionChanged =
      !!edit.connectorSelection &&
      !isSameConnectorSelection(edit.connectorSelection, connectorSelectionOfRow(row))
    return loadsChanged || selectionChanged
  })
